import { ArrowRightOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useRef } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import { Paragraph } from '@/components/Typography/Typo';
import {
  type HeySynclyChat,
  HeySynclyMessageDisplay,
} from '@/entities/hey-syncly';
import { DASHBOARD_CARD_ANALYSIS_ALIAS_DATASET_KEY } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import { useMessage } from '@/messages';
import service from '@/services';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import { useToast } from '@/stores/pure/ToastStore';

import { AnalysisType, ANLAYSIS_QUESTION_TYPE } from '../../types';

type Props = {
  onGptActionClick: (action: ANLAYSIS_QUESTION_TYPE) => void;
  chart_payload: ChartGptPayload;
  chart_name?: string;
  chart_alias: string;
  dashboard_alias: string;
  analysis_alias?: string;
  chat?: HeySynclyChat;
};

const PromptArea = (props: Props) => {
  const color = useColor();
  const answer = useHeySynclyStore((state) => state.tempAnswer);
  const chat = props.chat;
  const containerRef = useRef<HTMLDivElement>(null);
  const m = useMessage();

  return (
    <Style ref={containerRef}>
      <ToggleStage
        status={!!chat}
        className="pb-8"
        fallback={
          <VFlex>
            <Flex a="flex-start" g={6}>
              <AIIcon className="text-2xl" />
              <Flex f={1}>
                <Paragraph size={1} weight="m">
                  Any questions about '{props?.chart_name}'?
                </Paragraph>
              </Flex>
            </Flex>
            <VFlex padding="8px 0px 0px 30px" g={40}>
              <Paragraph size={1}>
                Add the answer to your dashboard as a Smart Brief, and get
                up-to-date information whenever the data updates.
              </Paragraph>
              <VFlex g={8} a="flex-start">
                <Button
                  sizeType="m"
                  variant="outlined"
                  rightIcon={<ArrowRightOutlined />}
                  rounded
                  style={{ backgroundColor: color.gray100 }}
                  onClick={() =>
                    props.onGptActionClick(ANLAYSIS_QUESTION_TYPE.SUMMARY)
                  }
                >
                  {m.heySyncly('GPT_TEMPLATE_SUMMARY') as string}
                </Button>
              </VFlex>
            </VFlex>
          </VFlex>
        }
      >
        {chat && (
          <div className="relative w-full">
            <HeySynclyMessageDisplay
              chat_id={chat.alias}
              messages={chat?.messages || []}
              status={chat?.status}
              answer={answer}
              additionalAction={({ chat_id: chatAlias, question }) => {
                return (
                  <AddToDashboardButton
                    {...props}
                    chat_id={chatAlias}
                    question={question || undefined}
                  />
                );
              }}
              onStudioItemClick={() => {}}
              container={containerRef.current}
              hideFeedbackCount
              hideReason
            />
          </div>
        )}
      </ToggleStage>
    </Style>
  );
};

const Style = styled(VFlex)`
  flex: 1;
  position: relative;
`;

type AddToDashboardProps = Props & {
  chat_id: string;
  question?: string;
};

const AddToDashboardButton = (props: AddToDashboardProps) => {
  const queryClient = useQueryClient();
  const addToast = useToast();

  const onAddToDashboardClick = async () => {
    const scrollToTarget = (analysis_alias: string) => {
      const element = document.querySelector(
        `[${DASHBOARD_CARD_ANALYSIS_ALIAS_DATASET_KEY}="${analysis_alias}"]`,
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('focus');
        setTimeout(() => {
          element.classList.remove('focus');
        }, 1000);
      }
    };

    if (!props.chart_payload) return;
    if (props.analysis_alias) {
      await service.analysis.updateAnalysisQuestion(
        props.dashboard_alias,
        props.analysis_alias,
        {
          chat_id: props.chat_id,
          question: props.question,
          question_type: ANLAYSIS_QUESTION_TYPE.QUESTION,
          chart_payload: props.chart_payload,
        },
      );

      await queryClient.refetchQueries({ queryKey: ['dashboard2', props.dashboard_alias] });
      scrollToTarget(props.analysis_alias);
      addToast('Analysis updated', 'success');
    } else {
      const analysis = await service.analysis.createAnalysis(
        props.dashboard_alias,
        {
          chat_id: props.chat_id,
          question: props.question,
          reference_id: props.chart_alias,
          reference_type: AnalysisType.CHART,
          chart_payload: props.chart_payload,
          question_type: ANLAYSIS_QUESTION_TYPE.QUESTION,
        },
      );
      await queryClient.refetchQueries({ queryKey: ['dashboard2', props.dashboard_alias] });
      scrollToTarget(analysis.alias);
      addToast('Added to dashboard', 'success');
    }
  };

  return (
    <Button
      variant="primary"
      sizeType="s"
      leftIcon={<Plus />}
      onClick={onAddToDashboardClick}
    >
      {props.analysis_alias ? 'Apply' : 'Add as smart brief'}
    </Button>
  );
};
export default PromptArea;
